import { useState } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../redux/store/store";
import { logout } from "../redux/slice/layoutSlice";
import { logout as clearAuthSession } from "../redux/slice/authSlice";
import { useCancelAccountDeletionMutation } from "../redux/api";
import type { AccountPendingDeletion as AccountPendingDeletionType } from "../redux/api";
import { IconTrash } from "../components/icons";

interface AccountPendingDeletionProps {
  pendingDeletion: AccountPendingDeletionType;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

// Shown right after login (before the org lobby/subscription gate/app shell)
// whenever the signed-in user has an open self-service deletion request —
// the account still logs in (that's how "restore" works) but shows only this
// screen until they restore it or the grace period elapses.
export const AccountPendingDeletion = ({ pendingDeletion }: AccountPendingDeletionProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [cancelAccountDeletion, { isLoading: restoring }] = useCancelAccountDeletionMutation();
  const [error, setError] = useState<string | null>(null);

  const handleRestore = async () => {
    setError(null);
    try {
      await cancelAccountDeletion().unwrap();
    } catch (err) {
      setError((err as { message?: string })?.message ?? "Failed to restore account.");
    }
  };

  const handleLogout = () => {
    dispatch(clearAuthSession());
    dispatch(logout());
  };

  return (
    <div className="login">
      <div className="login__card">
        <span className="login__logo">
          <IconTrash size={26} />
        </span>
        <h1 className="login__title">Account scheduled for deletion</h1>
        <p className="login__sub">
          Your account is scheduled to be permanently deleted on{" "}
          <strong>{formatDate(pendingDeletion.scheduledFor)}</strong>. You can restore it any time
          before then.
        </p>

        {error ? <p className="login__hint login__hint--error">{error}</p> : null}

        <button
          type="button"
          className="btn btn--primary login__submit"
          onClick={handleRestore}
          disabled={restoring}
        >
          {restoring ? "Restoring…" : "Restore my account"}
        </button>

        <button type="button" className="btn btn--ghost login__back" onClick={handleLogout}>
          Log out
        </button>
      </div>
    </div>
  );
};

export default AccountPendingDeletion;
